import { useEffect, useRef, useState } from 'react'
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa'
import styled from 'styled-components'
import moment from 'moment'

const toYMD = (s?: string) => (s ? s.slice(0, 10) : '')

// Convert YYYY-MM-DD to MM/DD/YYYY using moment
const formatDateDisplay = (ymd: string): string => {
  if (!ymd) return ''
  return moment(ymd, 'YYYY-MM-DD').format('MM/DD/YYYY')
}

type DateComparisonMode = 'range' | 'less-than' | 'greater-than'

type DateRangeFieldProps = {
  valueFrom: string
  valueTo: string
  operator?: 'range' | 'less-than' | 'greater-than'
  onChange: (from: string, to: string, operator: 'range' | 'less-than' | 'greater-than') => void
  label?: string
}

export function DateRangeField({ valueFrom, valueTo, operator, onChange, label = 'Date Range' }: DateRangeFieldProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(valueFrom)
  const [draftTo, setDraftTo] = useState(valueTo)

  const determineMode = (from: string, to: string, op?: 'range' | 'less-than' | 'greater-than'): DateComparisonMode => {
    if (op) return op
    if (from && to) return 'range'
    if (from && !to) return 'greater-than'
    if (!from && to) return 'less-than'
    return 'range'
  }

  const [mode, setMode] = useState<DateComparisonMode>(determineMode(valueFrom, valueTo, operator))

  const today = new Date().toISOString().slice(0, 10)

  // Normalized YMD values for validation (from drafts)
  const fromYMD = toYMD(draftFrom)
  const toYMDVal = toYMD(draftTo)

  // Normalized YMD values for display (from committed values)
  const displayFromYMD = toYMD(valueFrom)
  const displayToYMD = toYMD(valueTo)

  useEffect(() => {
    setDraftFrom(valueFrom)
    setDraftTo(valueTo)
    setMode(determineMode(valueFrom, valueTo, operator))
  }, [valueFrom, valueTo, operator])

  useEffect(() => {
    if (open) {
      // When opening, make sure draft values are synced with current values
      setDraftFrom(valueFrom)
      setDraftTo(valueTo)
      setMode(determineMode(valueFrom, valueTo, operator))
    }
  }, [open, valueFrom, valueTo, operator])

  const handleModeChange = (newMode: DateComparisonMode) => {
    setMode(newMode)
    if (newMode === 'less-than') {
      setDraftFrom('')
    } else if (newMode === 'greater-than') {
      setDraftTo('')
    }
  }

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const node = e.target as Node
      if (hostRef.current && hostRef.current.contains(node)) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // --- Validation rules ---
  // 1. From date can't be in the future
  // 2. To date can't be in the future
  // 3. From date can't be after To date (only for range mode)
  let error: string | null = null
  if (mode === 'greater-than' && fromYMD && fromYMD > today) {
    error = 'Date cannot be in the future.'
  } else if (mode === 'less-than' && toYMDVal && toYMDVal > today) {
    error = 'Date cannot be in the future.'
  } else if (mode === 'range') {
    if (fromYMD && fromYMD > today) {
      error = 'From date cannot be in the future.'
    } else if (toYMDVal && toYMDVal > today) {
      error = 'To date cannot be in the future.'
    } else if (fromYMD && toYMDVal && fromYMD > toYMDVal) {
      error = '"From" date cannot be after "To" date.'
    }
  }

  const apply = () => {
    if (error) return // safety guard, Apply button is also disabled

    let from = ''
    let to = ''

    if (mode === 'range') {
      from = fromYMD
      to = toYMDVal
      if (from && to && from > to) {
        onChange(to, from, mode)
        setOpen(false)
        return
      }
    } else if (mode === 'greater-than') {
      from = fromYMD
      to = ''
    } else if (mode === 'less-than') {
      from = ''
      to = toYMDVal
    }

    onChange(from, to, mode)
    setOpen(false)
  }

  const cancel = () => {
    setDraftFrom(valueFrom)
    setDraftTo(valueTo)
    setMode(determineMode(valueFrom, valueTo, operator))
    setOpen(false)
  }

  const display =
    mode === 'range' && displayFromYMD && displayToYMD
      ? `${formatDateDisplay(displayFromYMD)} → ${formatDateDisplay(displayToYMD)}`
      : mode === 'greater-than' && displayFromYMD
      ? `≥ ${formatDateDisplay(displayFromYMD)}`
      : mode === 'less-than' && displayToYMD
      ? `≤ ${formatDateDisplay(displayToYMD)}`
      : ''

  return (
    <DateRangeContainer ref={hostRef}>
      <FieldLabel>{label}</FieldLabel>
      <DateRangeBox hasValue={!!display} onClick={() => setOpen((o) => !o)} aria-haspopup="dialog" aria-expanded={open}>
        <FaCalendarAlt />
        <span>{display || 'Select range…'}</span>
        <FaChevronDown />
      </DateRangeBox>

      {open && (
        <DateRangePanel role="dialog" aria-label={`${label} picker`}>
          <DateRangePanelTitle>Choose Type</DateRangePanelTitle>

          <ModeSelector>
            <ModeOption selected={mode === 'less-than'} onClick={() => handleModeChange('less-than')}>
              Less Than
            </ModeOption>
            <ModeOption selected={mode === 'greater-than'} onClick={() => handleModeChange('greater-than')}>
              Greater Than
            </ModeOption>

            <ModeOption selected={mode === 'range'} onClick={() => handleModeChange('range')}>
              In Range
            </ModeOption>
          </ModeSelector>

          <DateRangePanelRow>
            {(mode === 'greater-than' || mode === 'range') && (
              <DateInputGroup>
                <span>{mode === 'range' ? 'From' : 'Date'}</span>
                <DateInput
                  type="date"
                  max={today}
                  value={fromYMD}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  placeholder="MM/DD/YYYY"
                />
              </DateInputGroup>
            )}
            {(mode === 'less-than' || mode === 'range') && (
              <DateInputGroup>
                <span>{mode === 'range' ? 'To' : 'Date'}</span>
                <DateInput
                  type="date"
                  max={today}
                  value={toYMDVal}
                  onChange={(e) => setDraftTo(e.target.value)}
                  placeholder="MM/DD/YYYY"
                />
              </DateInputGroup>
            )}
          </DateRangePanelRow>

          {error && <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>{error}</div>}

          <DateRangeActions>
            <ActionButton type="button" onClick={cancel}>
              Cancel
            </ActionButton>
            <ActionButton
              type="button"
              isPrimary
              onClick={apply}
              disabled={
                !!error ||
                (mode === 'range' && (!fromYMD || !toYMDVal)) ||
                (mode === 'greater-than' && !fromYMD) ||
                (mode === 'less-than' && !toYMDVal)
              }
            >
              Apply
            </ActionButton>
          </DateRangeActions>
        </DateRangePanel>
      )}
    </DateRangeContainer>
  )
}

/* ---------- Styled Components ---------- */
const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1.3 1 190px;
  min-width: 190px;
  position: relative;
  overflow: visible;
`

const FieldLabel = styled.label`
  ${({ theme }) => theme.typography.s.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xxxs};
`

const DateRangeBox = styled.div<{ hasValue: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxxs};
  height: 32px;
  border: 1px solid ${({ theme, hasValue }) => (hasValue ? theme.colors.primary.dark : theme.colors.grey.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  background: ${({ theme }) => theme.colors.neutral.lightest};
  cursor: pointer;

  span {
    color: ${({ theme, hasValue }) => (hasValue ? theme.colors.grey.darker : theme.colors.grey.neutral)};
  }

  svg:last-child {
    margin-left: auto;
    font-size: 12px;
    opacity: 0.8;
  }
`

const DateRangePanel = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 2000;
  min-width: 280px;
  background: ${({ theme }) => theme.colors.neutral.lightest};
  border: 1px solid ${({ theme }) => theme.colors.neutral.light};
  border-radius: ${({ theme }) => theme.spacing.xxs};
  box-shadow: ${({ theme }) => theme.shadows.light};
  padding: ${({ theme }) => theme.spacing.xs};
`

const DateRangePanelTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  ${({ theme }) => theme.typography.xs.bold};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`

const ModeSelector = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.grey.light};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  overflow: hidden;
`

const ModeOption = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.xxxs} ${({ theme }) => theme.spacing.xxs};
  ${({ theme }) => theme.typography.xxs.medium};
  background: ${({ theme, selected }) => (selected ? theme.colors.primary.neutral : theme.colors.neutral.lightest)};
  color: ${({ theme, selected }) => (selected ? theme.colors.neutral.lightest : theme.colors.grey.darker)};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.grey.light};
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${({ theme, selected }) => (selected ? theme.colors.primary.dark : theme.colors.grey.lightest)};
  }
`

export const DateRangePanelRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`

export const DateInputGroup = styled.div`
  display: grid;
  gap: 4px;

  span {
    ${({ theme }) => theme.typography.xxs.medium};
  }
`

export const DateInput = styled.input`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxs};
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.grey.light};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.s.normal};
  background: ${({ theme }) => theme.colors.neutral.lightest};
`

const DateRangeActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xxs};
  margin-top: 4px;
`

export const ActionButton = styled.button<{ isPrimary?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xxxs} ${({ theme }) => theme.spacing.xs};
  ${({ theme }) => theme.typography.xs.normal};
  border: 1px solid ${({ theme }) => theme.colors.neutral.light};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  background: ${({ theme, isPrimary }) => (isPrimary ? theme.colors.primary.neutral : theme.colors.neutral.lightest)};
  color: ${({ theme, isPrimary }) => (isPrimary ? theme.colors.neutral.lightest : 'inherit')};
  border-color: ${({ theme, isPrimary }) => (isPrimary ? theme.colors.primary.neutral : theme.colors.neutral.light)};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
